import { PARTS } from '@vm/shared';
import { buildCharacter, CHAR_PALETTE, decodeSkin, VoxelKind } from './characters';
import { CAMO_BY_ID } from './cosmetics';
import { buildWeaponModel, type VoxelModel } from './models/weapons';
import { WEAPON_BY_ID } from './weapons';

/**
 * Tiny 2D renderers that turn the voxel data into crisp pixel-art thumbnails (weapon side
 * views and marine front views) without spending a WebGL context per card. Results are
 * cached as data URLs. Browser only (canvas): imported as `@vm/content/sprites` by the game
 * and the admin console, never from the package index.
 */

const cache = new Map<string, string>();
const MAX_CACHE = 400;

function remember(key: string, url: string): string {
  if (cache.size >= MAX_CACHE) {
    const first = cache.keys().next().value;
    if (first !== undefined) cache.delete(first);
  }
  cache.set(key, url);
  return url;
}

function rgb(c: number, k = 1, add = 0): string {
  const r = Math.max(0, Math.min(255, Math.round(((c >> 16) & 255) * k + add)));
  const g = Math.max(0, Math.min(255, Math.round(((c >> 8) & 255) * k + add)));
  const b = Math.max(0, Math.min(255, Math.round((c & 255) * k + add)));
  return `rgb(${r},${g},${b})`;
}

interface Pixel {
  color: number;
  depth: number;
  glow: boolean;
}

/** Draws a depth-shaded pixel grid with bevelled voxel edges and a soft outline. */
function paint(grid: (Pixel | null)[], w: number, h: number, scale: number, pad: number): string {
  const canvas = document.createElement('canvas');
  canvas.width = w * scale + pad * 2;
  canvas.height = h * scale + pad * 2;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  let dmin = Infinity;
  let dmax = -Infinity;
  for (const p of grid) {
    if (!p) continue;
    dmin = Math.min(dmin, p.depth);
    dmax = Math.max(dmax, p.depth);
  }
  const at = (x: number, y: number): Pixel | null => (x < 0 || y < 0 || x >= w || y >= h ? null : grid[x + y * w]);
  // Outline pass.
  ctx.fillStyle = 'rgba(8,10,18,0.55)';
  const o = Math.max(1, Math.round(scale / 3));
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) if (at(x, y)) ctx.fillRect(pad + x * scale - o, pad + y * scale - o, scale + o * 2, scale + o * 2);
  const b = Math.max(1, Math.round(scale / 5));
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const p = at(x, y);
      if (!p) continue;
      const depthK = dmax > dmin ? (p.depth - dmin) / (dmax - dmin) : 0;
      const k = p.glow ? 1.15 : 1 - depthK * 0.28;
      const px = pad + x * scale;
      const py = pad + y * scale;
      ctx.fillStyle = rgb(p.color, k, p.glow ? 20 : 0);
      ctx.fillRect(px, py, scale, scale);
      if (scale < 3) continue;
      const up = at(x, y - 1);
      const left = at(x - 1, y);
      const down = at(x, y + 1);
      const right = at(x + 1, y);
      if (!up || up.depth > p.depth) {
        ctx.fillStyle = 'rgba(255,255,255,0.28)';
        ctx.fillRect(px, py, scale, b);
      }
      if (!left || left.depth > p.depth) {
        ctx.fillStyle = 'rgba(255,255,255,0.14)';
        ctx.fillRect(px, py, b, scale);
      }
      if (!down || down.depth > p.depth) {
        ctx.fillStyle = 'rgba(0,0,0,0.3)';
        ctx.fillRect(px, py + scale - b, scale, b);
      }
      if (!right || right.depth > p.depth) {
        ctx.fillStyle = 'rgba(0,0,0,0.2)';
        ctx.fillRect(px + scale - b, py, b, scale);
      }
    }
  return canvas.toDataURL('image/png');
}

// Marines -------------------------------------------------------------------------------------------------

/**
 * Front view of a marine. 'head' crops to the helmet/face for avatars; `bare` leaves the armor
 * shell off so the painted skin (and face) is fully visible.
 */
export function skinSprite(skin: string, armor: string, mode: 'full' | 'head' = 'full', scale = 6, bare = false): string {
  const key = `c:${mode}:${scale}:${bare ? '-' : armor}:${skin}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  const grids = buildCharacter(decodeSkin(skin), armor, -1);
  const [x0, x1, y0, y1] = mode === 'head' ? [-5, 4, 24, 32] : [-9, 8, 0, 32];
  const w = x1 - x0 + 1;
  const h = y1 - y0 + 1;
  const px: (Pixel | null)[] = new Array(w * h).fill(null);
  for (const g of grids) {
    const def = PARTS[g.part];
    if (mode === 'head' && g.part !== 0) continue;
    for (let gy = 0; gy < g.sy; gy++)
      for (let gz = 0; gz < g.sz; gz++)
        for (let gx = 0; gx < g.sx; gx++) {
          const i = gx + gz * g.sx + gy * g.sx * g.sz;
          if (!g.kind[i] || (bare && g.kind[i] === VoxelKind.Armor)) continue;
          const x = gx + def.min[0];
          const y = gy + def.min[1];
          const z = gz + def.min[2];
          if (x < x0 || x > x1 || y < y0 || y > y1) continue;
          // Viewer stands in front (characters face -Z): mirror x so their right is on our left.
          const u = x1 - x;
          const v = y1 - y;
          const cur = px[u + v * w];
          if (!cur || z < cur.depth) {
            const ci = g.color[i];
            px[u + v * w] = { color: CHAR_PALETTE[ci], depth: z, glow: ci >= 130 };
          }
        }
  }
  return remember(key, paint(px, w, h, scale, Math.max(2, scale)));
}

// Weapons ------------------------------------------------------------------------------------------------

function shade(c: number, k: number): number {
  const r = Math.min(255, Math.round(((c >> 16) & 255) * k));
  const g = Math.min(255, Math.round(((c >> 8) & 255) * k));
  const b = Math.min(255, Math.round((c & 255) * k));
  return (r << 16) | (g << 8) | b;
}

function camoPalette(model: VoxelModel, camoId?: string): number[] {
  const camo = camoId ? CAMO_BY_ID[camoId] : undefined;
  if (!camo || camo.id === 'none') return model.palette;
  const p = [...model.palette];
  p[1] = camo.colors[0];
  p[2] = camo.colors[1];
  p[3] = camo.colors[2];
  p[4] = camo.glow ?? camo.colors[2];
  p[6] = shade(camo.colors[0], 1.25);
  return p;
}

/**
 * Side view of a weapon model (muzzle pointing right). Layers: the gun's voxels, its fine hardware (detail layer) and a fitted
 * sight; the picture is drawn at the finest resolution among them so thin parts stay crisp.
 */
export function weaponIcon(weaponId: string, camoId?: string, scale = 4, scopeId?: string): string {
  const key = `w:${scale}:${weaponId}:${camoId ?? ''}:${scopeId ?? ''}`;
  const hit = cache.get(key);
  if (hit !== undefined) return hit;
  const def = WEAPON_BY_ID[weaponId];
  if (!def) return remember(key, '');
  const model = buildWeaponModel(def.model, undefined, scopeId);
  const pal = camoPalette(model, camoId);
  const { sx, sy, sz, grid } = model;
  const at = model.attach;
  const dl = model.detail;
  // Pixels per gun voxel: the finest layer present (gun voxels become res x res blocks).
  const res = Math.max(at ? at.res : 1, dl ? dl.res : 1);
  const glowOf = (v: number): boolean => model.emissive.includes(v);
  const gunAt = (x: number, y: number, z: number): number => (x < 0 || y < 0 || z < 0 || x >= sx || y >= sy || z >= sz ? 0 : grid[x + z * sx + y * sx * sz]);
  // A layer with `lr` cells per gun voxel, sampled at a pixel (Y/Z in 1/res gun voxels): its cell index range along that pixel.
  const cellAt = (lr: number, P: number): number => Math.floor((P * lr) / res);
  let zMin = sz * res;
  let zMax = -1;
  let yMin = Infinity;
  let yMax = -1;
  const grow = (Y: number, Z: number): void => {
    zMin = Math.min(zMin, Z);
    zMax = Math.max(zMax, Z);
    yMin = Math.min(yMin, Y);
    yMax = Math.max(yMax, Y);
  };
  for (let y = 0; y < sy; y++)
    for (let z = 0; z < sz; z++)
      for (let x = 0; x < sx; x++)
        if (gunAt(x, y, z)) {
          grow(y * res, z * res);
          grow(y * res + res - 1, z * res + res - 1);
        }
  if (dl) {
    const k = res / dl.res;
    for (let y = 0; y < dl.sy; y++)
      for (let z = 0; z < dl.sz; z++)
        for (let x = 0; x < dl.sx; x++)
          if (dl.grid[x + z * dl.sx + y * dl.sx * dl.sz]) {
            grow(Math.floor(y * k), Math.floor(z * k));
            grow(Math.floor((y + 1) * k) - 1, Math.floor((z + 1) * k) - 1);
          }
  }
  if (at) {
    const k = res / at.res;
    for (let y = 0; y < at.sy; y++)
      for (let z = 0; z < at.sz; z++)
        for (let x = 0; x < at.sx; x++)
          if (at.grid[x + z * at.sx + y * at.sx * at.sz]) {
            const Y = at.origin[1] * res + Math.floor(y * k);
            grow(Y, Math.floor(z * k));
            grow(at.origin[1] * res + Math.floor((y + 1) * k) - 1, Math.floor((z + 1) * k) - 1);
          }
  }
  if (zMax < 0) return remember(key, '');
  const w = zMax - zMin + 1;
  const h = yMax - yMin + 1;
  const px: (Pixel | null)[] = new Array(w * h).fill(null);
  const cell = Math.max(1, Math.round(scale / res));
  for (let Y = yMin; Y <= yMax; Y++)
    for (let Z = zMin; Z <= zMax; Z++) {
      let best: Pixel | null = null;
      // Gun: nearest solid voxel from the viewer (+X side).
      const gy = Math.floor(Y / res);
      const gz = Math.floor(Z / res);
      for (let x = sx - 1; x >= 0; x--) {
        const v = gunAt(x, gy, gz);
        if (!v) continue;
        best = { color: pal[v] ?? 0x888888, depth: sx - 1 - x, glow: glowOf(v) };
        break;
      }
      // Fine hardware: in front when nearer the viewer (or level with it).
      if (dl) {
        const dy = cellAt(dl.res, Y);
        const dz = cellAt(dl.res, Z);
        if (dy >= 0 && dy < dl.sy && dz >= 0 && dz < dl.sz) {
          for (let x = dl.sx - 1; x >= 0; x--) {
            const v = dl.grid[x + dz * dl.sx + dy * dl.sx * dl.sz];
            if (!v) continue;
            const depth = sx - 1 - x / dl.res;
            if (!best || depth <= best.depth) best = { color: pal[v] ?? 0x888888, depth, glow: glowOf(v) };
            break;
          }
        }
      }
      // Sight: finer cells; in front of the gun when nearer the viewer.
      if (at) {
        const ay = Math.floor(((Y - at.origin[1] * res) * at.res) / res);
        const az = cellAt(at.res, Z);
        if (ay >= 0 && ay < at.sy && az >= 0 && az < at.sz) {
          for (let x = at.sx - 1; x >= 0; x--) {
            const v = at.grid[x + az * at.sx + ay * at.sx * at.sz];
            if (!v) continue;
            const depth = sx - 1 - x / at.res;
            if (!best || depth <= best.depth) best = { color: pal[v] ?? 0x888888, depth, glow: glowOf(v) };
            break;
          }
        }
      }
      // Muzzle (z = 0) on the right, viewer on the +X side.
      if (best) px[zMax - Z + (yMax - Y) * w] = best;
    }
  return remember(key, paint(px, w, h, cell, cell));
}
